import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import SearchableMultiSelect from '@/components/SearchableMultiSelect';
import api from '@/services/api';
import { Loader2 } from 'lucide-react';

// Create-audit form: nothing is created until "Create audit" is pressed.
export const CreateAuditDialog = ({ open, onOpenChange, locationType, locationId, onCreated }) => {
  const [opts, setOpts] = useState(null);
  const [form, setForm] = useState({ title: '', category_id: '', brand_ids: [], vendor_ids: [], notes: '', include_zero_stock: false });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    if (!open || !locationId) return;
    setForm({ title: '', category_id: '', brand_ids: [], vendor_ids: [], notes: '', include_zero_stock: false }); setErr('');
    setOpts(null);
    api.get('/stock-audit/meta/options', { params: { location_type: locationType, location_id: locationId } })
      .then(r => setOpts(r.data)).catch(() => setOpts({ brands: [], vendors: [], categories: [], product_count: 0 }));
  }, [open, locationType, locationId]);

  const submit = async () => {
    setBusy(true); setErr('');
    try {
      const r = await api.post('/stock-audit', {
        location_type: locationType, location_id: locationId, title: form.title || undefined, notes: form.notes || undefined,
        category_id: form.category_id || undefined, brand_ids: form.brand_ids, vendor_ids: form.vendor_ids, include_zero_stock: form.include_zero_stock,
      });
      onCreated(r.data); onOpenChange(false);
    } catch (e) { setErr(e.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white max-w-lg" data-testid="create-audit-dialog">
        <DialogHeader>
          <DialogTitle>New stock audit</DialogTitle>
          <DialogDescription>Choose what to count. {opts ? `${opts.product_count} active products are in stock at this location.` : 'Loading products…'}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div><Label>Title</Label><Input value={form.title} onChange={(e) => setForm(f => ({ ...f, title: e.target.value }))} placeholder="e.g. Monthly audit — June" data-testid="create-audit-title" /></div>
          <div><Label>Brands (optional)</Label>
            <SearchableMultiSelect items={opts?.brands || []} selectedIds={form.brand_ids} onChange={(ids) => setForm(f => ({ ...f, brand_ids: ids }))} placeholder="All brands" color="purple" testId="create-audit-brands" />
          </div>
          <div><Label>Suppliers / vendors (optional)</Label>
            <SearchableMultiSelect items={opts?.vendors || []} selectedIds={form.vendor_ids} onChange={(ids) => setForm(f => ({ ...f, vendor_ids: ids }))} placeholder="All suppliers" color="teal" testId="create-audit-vendors" />
          </div>
          <div><Label>Category (optional)</Label>
            <Select value={form.category_id || 'all'} onValueChange={(v) => setForm(f => ({ ...f, category_id: v === 'all' ? '' : v }))}>
              <SelectTrigger data-testid="create-audit-category"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white"><SelectItem value="all">All categories</SelectItem>{(opts?.categories || []).map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <label className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm cursor-pointer">
            <input type="checkbox" className="mt-0.5" checked={form.include_zero_stock} onChange={(e) => setForm(f => ({ ...f, include_zero_stock: e.target.checked }))} data-testid="create-audit-zero-stock" />
            <span><b>Include out-of-stock products</b> — audit every active product{opts?.active_product_count ? ` (${opts.active_product_count} total)` : ''}, even with 0 stock here. Use this to count stock sold in store without a transfer; mark such lines <i>Internal Sales Stock</i> and the Store Manager can post them directly.</span>
          </label>
          <div><Label>Notes</Label><Input value={form.notes} onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))} /></div>
          {err && <p className="text-sm text-red-600" data-testid="create-audit-error">{err}</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button className="bg-emerald-600 hover:bg-emerald-700" disabled={busy || !locationId || !opts} onClick={submit} data-testid="create-audit-submit">
            {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Create audit
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CreateAuditDialog;
